import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Play, Flame } from "lucide-react";
import { api } from "@/lib/api";

const fmt = (s) => {
  const n = Math.max(0, Math.round(Number(s) || 0));
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`;
};

// cc_16/cc_17: system message sys_kind="replay" → tappable replay card that deep-links to the BLOQ's
// recordings and seeks to the top moment. Shows "▶ N members caught the replay" when N > 0.
function ReplayCard({ msg, bloqId }) {
  const navigate = useNavigate();
  const meta = msg.meta || {};
  const { data } = useQuery({
    queryKey: ["recording-listens", meta.recording_id],
    queryFn: () => api.recordingListens(meta.recording_id),
    enabled: !!meta.recording_id,
    staleTime: 30000,
    retry: false,
  });
  const listens = data?.listens ?? meta.replay_listens ?? 0;
  const open = () => {
    if (!bloqId) return;
    const q = new URLSearchParams({ info: "1", play: meta.recording_id || "", t: String(meta.top_moment_s || 0) });
    navigate(`/app/bloqs/${bloqId}?${q}`);
  };
  return (
    <button
      type="button"
      onClick={open}
      className="mx-auto flex w-full max-w-sm items-center gap-3 rounded-2xl border border-bloq-purple/40 bg-gradient-to-br from-bloq-purple/20 to-bloq-s2 p-3 text-left transition-transform hover:scale-[1.01]"
      data-testid={`replay-card-${msg.id}`}
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-bloq-purple/25 text-bloq-purple"><Play className="h-5 w-5 fill-current" /></span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-bold text-white">{meta.room_title ? `Replay · ${meta.room_title}` : "Room replay is ready"}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-bloq-muted">
          {meta.top_moment_s != null && <span className="inline-flex items-center gap-1 text-amber-300"><Flame className="h-3 w-3" /> Top moment at {fmt(meta.top_moment_s)}{meta.top_moment_count ? ` · ${meta.top_moment_count} reactions` : ""}</span>}
          {listens > 0 && <span data-testid={`replay-listens-${msg.id}`}>▶ {listens} {listens === 1 ? "member" : "members"} caught the replay</span>}
        </div>
      </div>
    </button>
  );
}

// Plain system pill. Unknown sys_kind values MUST fall back here (§18 rule).
export function SystemMessage({ msg, bloqId }) {
  if (msg.sys_kind === "replay" && msg.meta?.recording_id) return <ReplayCard msg={msg} bloqId={bloqId} />;
  return (
    <div className="mx-auto w-fit max-w-[85%] rounded-full border border-white/8 bg-bloq-s2 px-3 py-1 text-center text-[11px] text-bloq-muted" data-testid={`system-msg-${msg.id}`}>
      {msg.text || "Updated"}
    </div>
  );
}
